import {
  Column,
  ColumnFilterElementTemplateOptions,
  ColumnProps,
} from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { TFunction } from 'i18next';

import { formatDate } from '@/utils/formatDate';
import InputDateFilter from '@/components/InputDateFilter/InputDateFilter';
import { getServiceCell } from '@/components/exchanges/ExchangesTable/getServiceCell';

import TableDropdownFilter from '@/components/TableDropdownFilter/TableDropdownFilter';

import {
  filterApplyTemplate,
  filterClearTemplate,
  inputTextTemplate,
} from '@/components/Table/Table';
import { TableData } from '@/components/Table/types';
import { TransactionDetail } from './DocumentsType';

export const tableColumns: ColumnProps[] = [
  {
    field: 'tipoLiquidacion',
    header: 'Tipo de Liquidación',
    filter: true,
    sortable: true,
    showFilterMenu: true,
  },
  {
    field: 'numeroDeLiquidacion',
    header: 'Número',
    sortable: true,
    style: { fontWeight: 'bold' },
    showFilterMenu: true,
    body: (rowData: TransactionDetail) =>
      getServiceCell(rowData.numeroDeLiquidacion.toString()),
  },
  {
    field: 'observacionesLiquidacion',
    header: 'Referencia',
    filter: true,
    showFilterMenu: true,
    style: { fontWeight: 'bold' },
  },
  {
    field: 'contenedor',
    header: 'Contenedor',
    filter: true,
    showFilterMenu: true,
    sortable: true,
    body: (rowData: TransactionDetail) => {
      const { contenedor = '' } = rowData || {};
      if (typeof contenedor !== 'string') return '';
      const [container] = contenedor.split(' ');
      const body = container.replace('/', '').replace('-', '');

      return body;
    },
  },
  {
    field: 'armador',
    header: 'Armador',
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'numero',
    header: 'Documento',
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'importe',
    header: 'Importe (USD)',
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'fechaDocumento',
    header: 'Fecha doc.',
    filter: true,
    body: (rowData: TransactionDetail) => formatDate(rowData.fechaDocumento),
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'vencimiento',
    header: 'Vencimiento',
    filter: true,
    body: (rowData: TransactionDetail) => formatDate(rowData.vencimiento),
    showFilterMenu: true,
    sortable: true,
  },
  {
    field: 'cliente',
    header: 'Cliente',
    filter: true,
    showFilterMenu: true,
    sortable: true,
  },
];

export const getHeaderColumnsGroup = (
  translates: TFunction<'Documents', undefined>,
  data: TransactionDetail[] | undefined = []
) => (
  <ColumnGroup>
    <Row>
      <Column
        field="tipoLiquidacion"
        header={translates('settlementNumber')}
        rowSpan={3}
        sortable
        style={{ borderTopLeftRadius: '16px' }}
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <TableDropdownFilter
            options={options}
            data={data as unknown as TableData}
            fieldName="tipoLiquidacion"
          />
        )}
      />

      <Column
        header={translates('number')}
        field="numeroDeLiquidacion"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header={translates('reference')}
        field="observacionesLiquidacion"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
      />
      <Column
        header={translates('container')}
        field="contenedor"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
      />
      <Column
        header={translates('shipOwner')}
        field="armador"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <TableDropdownFilter
            options={options}
            data={data as unknown as TableData}
            fieldName="armador"
          />
        )}
      />
      <Column
        header={translates('document')}
        field="numero"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header={translates('amountUSD')}
        field="importe"
        rowSpan={3}
        sortable
        showFilterMenu
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
      <Column
        header={translates('documentDate')}
        field="fechaDocumento"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
        filterField="fechaDocumento"
      />

      <Column
        header={translates('dueDate')}
        field="vencimiento"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={(options: ColumnFilterElementTemplateOptions) => (
          <InputDateFilter options={options} />
        )}
        dataType="date"
        filterField="vencimiento"
      />

      <Column
        header={translates('client')}
        field="cliente"
        sortable
        filter
        showFilterMatchModes={false}
        filterApply={filterApplyTemplate}
        filterClear={filterClearTemplate}
        filterElement={inputTextTemplate}
      />
    </Row>
  </ColumnGroup>
);
